/**
 * TipTap Rich Text Editor Component
 * Provides asset-type specific editing capabilities for HTML content
 */

import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
// import Table from '@tiptap/extension-table';
// import TableRow from '@tiptap/extension-table-row';
// import TableCell from '@tiptap/extension-table-cell';
// import TableHeader from '@tiptap/extension-table-header';
import {
  Bold, Italic, Underline, Strikethrough, Code, List, ListOrdered,
  Link2, Image as ImageIcon, Table as TableIcon, Undo, Redo,
  Heading1, Heading2, Heading3, Quote, Minus, AlignLeft, AlignCenter,
  AlignRight, AlignJustify, Mail, Globe, FileQuestion, FileText
} from 'lucide-react';

interface TipTapEditorProps {
  content: string;
  onChange: (html: string) => void;
  assetType: 'email' | 'seo_content' | 'faq' | 'site_content' | 'page_content';
  placeholder?: string;
  readOnly?: boolean;
}

// Email-specific toolbar
function EmailToolbar({ editor }: { editor: any }) {
  if (!editor) return null;

  return (
    <div className="border-b bg-gray-50 p-2 flex flex-wrap gap-1">
      <div className="flex items-center gap-1 pr-2 border-r">
        <Mail className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-600">Email Editor</span>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('strike') ? 'bg-gray-200' : ''}`}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''}`}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => {
            const url = window.prompt('Enter URL:');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-200' : ''}`}
          title="Add Link"
        >
          <Link2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            const url = window.prompt('Enter image URL:');
            if (url) {
              editor.chain().focus().setImage({ src: url }).run();
            }
          }}
          className="p-2 rounded hover:bg-gray-200"
          title="Add Image"
        >
          <ImageIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
          title="Undo"
        >
          <Undo className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
          title="Redo"
        >
          <Redo className="w-4 h-4" />
        </button>
      </div>

      {/* Email Templates */}
      <div className="flex items-center gap-1 px-2 border-l">
        <select
          className="px-2 py-1 text-sm border rounded"
          onChange={(e) => {
            if (e.target.value) {
              editor.chain().focus().setContent(getEmailTemplate(e.target.value)).run();
            }
          }}
        >
          <option value="">Insert Template...</option>
          <option value="newsletter">Newsletter</option>
          <option value="promotional">Promotional</option>
          <option value="transactional">Transactional</option>
          <option value="welcome">Welcome Email</option>
        </select>
      </div>
    </div>
  );
}

// SEO Content-specific toolbar
function SEOToolbar({ editor }: { editor: any }) {
  if (!editor) return null;

  const wordCount = editor.storage.characterCount?.words() || 0;

  return (
    <div className="border-b bg-gray-50 p-2 flex flex-wrap gap-1">
      <div className="flex items-center gap-1 pr-2 border-r">
        <Globe className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-600">SEO Editor</span>
        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
          {wordCount} words
        </span>
      </div>

      {/* Standard formatting tools */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
        >
          <Italic className="w-4 h-4" />
        </button>
      </div>

      {/* Heading hierarchy for SEO */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''}`}
          title="H1 - Page Title"
        >
          H1
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
          title="H2 - Section"
        >
          H2
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}`}
          title="H3 - Subsection"
        >
          H3
        </button>
      </div>

      {/* SEO-specific insertions */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => {
            const keyword = window.prompt('Enter target keyword to highlight:');
            if (keyword) {
              // Add highlighting logic here
              editor.chain().focus().insertContent(`<mark>${keyword}</mark>`).run();
            }
          }}
          className="px-2 py-1 text-sm border rounded hover:bg-gray-200"
        >
          Add Keyword
        </button>
        <button
          onClick={() => {
            editor.chain().focus().insertContent(
              '<div class="schema-markup" contenteditable="false">Schema Markup Placeholder</div>'
            ).run();
          }}
          className="px-2 py-1 text-sm border rounded hover:bg-gray-200"
        >
          Add Schema
        </button>
      </div>
    </div>
  );
}

// FAQ-specific toolbar
function FAQToolbar({ editor }: { editor: any }) {
  if (!editor) return null;

  return (
    <div className="border-b bg-gray-50 p-2 flex flex-wrap gap-1">
      <div className="flex items-center gap-1 pr-2 border-r">
        <FileQuestion className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-600">FAQ Editor</span>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => {
            editor.chain().focus().insertContent(
              `<div class="faq-item">
                <h3 class="faq-question">Question?</h3>
                <div class="faq-answer">Answer goes here...</div>
              </div>`
            ).run();
          }}
          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Add FAQ Item
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
        >
          <List className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Page Content toolbar - Full featured for article editing
function PageContentToolbar({ editor }: { editor: any }) {
  if (!editor) return null;

  return (
    <div className="border-b bg-gray-50 p-2 flex flex-wrap gap-1">
      <div className="flex items-center gap-1 pr-2 border-r">
        <FileText className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-600">Page Content</span>
      </div>

      {/* Text formatting */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('strike') ? 'bg-gray-200' : ''}`}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('code') ? 'bg-gray-200' : ''}`}
          title="Code"
        >
          <Code className="w-4 h-4" />
        </button>
      </div>

      {/* Headings - Full H1-H6 */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`px-2 py-1 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''}`}
          title="Heading 1"
        >
          H1
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`px-2 py-1 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
          title="Heading 2"
        >
          H2
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`px-2 py-1 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}`}
          title="Heading 3"
        >
          H3
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          className={`px-2 py-1 rounded hover:bg-gray-200 text-sm ${editor.isActive('heading', { level: 4 }) ? 'bg-gray-200' : ''}`}
          title="Heading 4"
        >
          H4
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
          className={`px-2 py-1 rounded hover:bg-gray-200 text-sm ${editor.isActive('heading', { level: 5 }) ? 'bg-gray-200' : ''}`}
          title="Heading 5"
        >
          H5
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
          className={`px-2 py-1 rounded hover:bg-gray-200 text-sm ${editor.isActive('heading', { level: 6 }) ? 'bg-gray-200' : ''}`}
          title="Heading 6"
        >
          H6
        </button>
      </div>

      {/* Lists and quotes */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('blockquote') ? 'bg-gray-200' : ''}`}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="p-2 rounded hover:bg-gray-200"
          title="Horizontal Rule"
        >
          <Minus className="w-4 h-4" />
        </button>
      </div>

      {/* Links and media */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => {
            const url = window.prompt('Enter URL:');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-200' : ''}`}
          title="Add Link"
        >
          <Link2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            const url = window.prompt('Enter image URL:');
            if (url) {
              editor.chain().focus().setImage({ src: url }).run();
            }
          }}
          className="p-2 rounded hover:bg-gray-200"
          title="Add Image"
        >
          <ImageIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Alignment */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''}`}
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''}`}
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200' : ''}`}
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'justify' }) ? 'bg-gray-200' : ''}`}
          title="Justify"
        >
          <AlignJustify className="w-4 h-4" />
        </button>
      </div>

      {/* Undo/Redo */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
          title="Undo"
        >
          <Undo className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
          title="Redo"
        >
          <Redo className="w-4 h-4" />
        </button>
      </div>

      {/* Clear formatting */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          className="px-3 py-1 text-sm border rounded hover:bg-gray-200"
          title="Clear Formatting"
        >
          Clear Format
        </button>
      </div>
    </div>
  );
}

// Site Content toolbar
function SiteContentToolbar({ editor }: { editor: any }) {
  if (!editor) return null;

  return (
    <div className="border-b bg-gray-50 p-2 flex flex-wrap gap-1">
      <div className="flex items-center gap-1 pr-2 border-r">
        <FileText className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-600">Site Content</span>
      </div>

      {/* Full formatting options */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('strike') ? 'bg-gray-200' : ''}`}
        >
          <Strikethrough className="w-4 h-4" />
        </button>
      </div>

      {/* Table support */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          className="p-2 rounded hover:bg-gray-200"
          title="Insert Table"
        >
          <TableIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Alignment */}
      <div className="flex items-center gap-1 px-2 border-l">
        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''}`}
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''}`}
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200' : ''}`}
        >
          <AlignRight className="w-4 h-4" />
        </button>
      </div>

      {/* Content blocks */}
      <div className="flex items-center gap-1 px-2 border-l">
        <select
          className="px-2 py-1 text-sm border rounded"
          onChange={(e) => {
            if (e.target.value) {
              editor.chain().focus().insertContent(getContentBlock(e.target.value)).run();
            }
          }}
        >
          <option value="">Insert Block...</option>
          <option value="hero">Hero Section</option>
          <option value="features">Features Grid</option>
          <option value="cta">Call to Action</option>
          <option value="testimonial">Testimonial</option>
        </select>
      </div>
    </div>
  );
}

// Main TipTap Editor Component
export function TipTapEditor({ content, onChange, assetType, placeholder, readOnly = false }: TipTapEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || getPlaceholder(assetType),
      }),
      // Table.configure({
      //   resizable: true,
      // }),
      // TableRow,
      // TableHeader,
      // TableCell,
    ],
    content,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      {/* Render appropriate toolbar based on asset type */}
      {!readOnly && (
        <>
          {assetType === 'email' && <EmailToolbar editor={editor} />}
          {assetType === 'seo_content' && <SEOToolbar editor={editor} />}
          {assetType === 'faq' && <FAQToolbar editor={editor} />}
          {assetType === 'site_content' && <SiteContentToolbar editor={editor} />}
          {assetType === 'page_content' && <PageContentToolbar editor={editor} />}
        </>
      )}

      {/* Editor content area */}
      <EditorContent
        editor={editor}
        className={`prose prose-lg max-w-none p-4 min-h-[400px] focus:outline-none ${
          readOnly ? 'bg-gray-50' : ''
        }`}
      />

      {/* Status bar */}
      <div className="border-t bg-gray-50 px-4 py-2 flex justify-between items-center text-sm text-gray-600">
        <div>
          {assetType === 'email' && 'Email HTML Editor'}
          {assetType === 'seo_content' && 'SEO-Optimized Content'}
          {assetType === 'faq' && 'FAQ Structure'}
          {assetType === 'site_content' && 'Website Content'}
          {assetType === 'page_content' && 'Page Article Content'}
        </div>
        {editor && (
          <div className="flex items-center gap-4">
            <span>{editor.storage.characterCount?.characters() || 0} characters</span>
            <span>{readOnly ? 'Read-only' : 'Editing'}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper functions
function getPlaceholder(assetType: string): string {
  switch (assetType) {
    case 'email':
      return 'Start writing your email content...';
    case 'seo_content':
      return 'Write SEO-optimized content. Remember to include your target keywords naturally...';
    case 'faq':
      return 'Add frequently asked questions and their answers...';
    case 'site_content':
      return 'Create engaging website content...';
    case 'page_content':
      return 'Write your article content here. Use headings to structure your content...';
    default:
      return 'Start writing...';
  }
}

function getEmailTemplate(templateType: string): string {
  switch (templateType) {
    case 'newsletter':
      return `
        <h1>Your Newsletter Title</h1>
        <p>Hi {{first_name}},</p>
        <p>Welcome to this month's newsletter! Here's what's new...</p>
        <h2>Featured Article</h2>
        <p>Article content goes here...</p>
        <p>Best regards,<br>Your Team</p>
      `;
    case 'promotional':
      return `
        <h1>Special Offer Just for You!</h1>
        <p>Dear {{first_name}},</p>
        <p>Don't miss out on our exclusive offer...</p>
        <a href="#">Shop Now</a>
      `;
    case 'welcome':
      return `
        <h1>Welcome to Our Community!</h1>
        <p>Hi {{first_name}},</p>
        <p>Thank you for joining us. We're excited to have you...</p>
      `;
    default:
      return '';
  }
}

function getContentBlock(blockType: string): string {
  switch (blockType) {
    case 'hero':
      return `
        <div class="hero-section">
          <h1>Hero Title</h1>
          <p>Hero subtitle or description</p>
          <a href="#" class="cta-button">Call to Action</a>
        </div>
      `;
    case 'features':
      return `
        <div class="features-grid">
          <div class="feature">
            <h3>Feature 1</h3>
            <p>Feature description</p>
          </div>
          <div class="feature">
            <h3>Feature 2</h3>
            <p>Feature description</p>
          </div>
        </div>
      `;
    case 'cta':
      return `
        <div class="cta-section">
          <h2>Ready to Get Started?</h2>
          <p>Take action today</p>
          <a href="#" class="button">Get Started</a>
        </div>
      `;
    default:
      return '';
  }
}

export default TipTapEditor;